/**
 * Helper functions para tracking de eventos no Segment
 * Estas funções podem ser chamadas de qualquer lugar, incluindo Redux effects
 */

/**
 * Envia evento de salvamento de preferências de loja para o Segment
 * @param {Object} params - Parâmetros do evento
 * @param {string} params.dealershipId - ID da loja
 * @param {string} params.dealershipName - Nome da loja
 * @param {string} params.email - Email da loja
 * @param {string} params.phoneNumber - Telefone da loja
 * @param {string} params.relatedNinja - Email do ninja responsável (pode ser vazio)
 * @param {string} params.actionType - 'create' ou 'update'
 * @param {number} params.estimatedNotifications - Número estimado de notificações
 * @param {string} params.structuralCondition - Condição estrutural escolhida
 * @param {string} params.auctionPreference - Preferência sobre leilão
 * @param {string} params.engineCondition - Condição do motor
 * @param {string} params.transmissionCondition - Condição do câmbio
 * @param {string} params.armoredPreference - Preferência sobre blindagem
 * @param {string} params.kmPreference - Preferência de quilometragem
 * @param {number|null} params.kmMaxValue - Valor máximo de km (numérico)
 * @param {string} params.agePreference - Preferência de idade
 * @param {number|null} params.ageMinYear - Ano mínimo desejado
 * @param {boolean} params.channelWhatsapp - Se WhatsApp está ativado
 * @param {boolean} params.channelEmail - Se Email está ativado
 * @param {boolean} params.channelPushNotification - Se Push Notification está ativado
 * @param {Array<string>} params.selectedBrands - Array com nomes das marcas selecionadas
 * @param {number} params.selectedBrandsCount - Quantidade de marcas selecionadas
 * @param {boolean} params.hasBrandPreference - Se tem alguma marca selecionada
 */
export const trackDealerPreferencesSaved = ({
  dealershipId,
  dealershipName,
  email,
  phoneNumber,
  relatedNinja,
  actionType,
  estimatedNotifications,
  structuralCondition,
  auctionPreference,
  engineCondition,
  transmissionCondition,
  armoredPreference,
  kmPreference,
  kmMaxValue,
  agePreference,
  ageMinYear,
  channelWhatsapp,
  channelEmail,
  channelPushNotification,
  selectedBrands,
  selectedBrandsCount,
  hasBrandPreference,
}) => {
  try {
    if (typeof window !== 'undefined' && window.analytics) {
      const eventData = {
        // Identificação da loja
        dealership_id: dealershipId,
        dealership_name: dealershipName,
        dealership_email: email,
        dealership_phone: phoneNumber,
        related_ninja: relatedNinja || '',

        // Metadados do evento
        date_hour: new Date().toISOString(),
        action_type: actionType, // 'create' ou 'update'
        estimated_notifications: estimatedNotifications || 0,

        // Preferências (respostas das 7 perguntas)
        structural_condition: structuralCondition,
        auction_preference: auctionPreference,
        engine_condition: engineCondition,
        transmission_condition: transmissionCondition,
        armored_preference: armoredPreference,
        km_preference: kmPreference,
        km_max_value: kmMaxValue || null,
        age_preference: agePreference,
        age_min_year: ageMinYear || null,

        // Canais de notificação
        channel_whatsapp: channelWhatsapp || false,
        channel_email: channelEmail || false,
        channel_push_notification: channelPushNotification || false,

        // Preferências de marcas
        selected_brands: selectedBrands || [],
        selected_brands_count: selectedBrandsCount || 0,
        has_brand_preference: hasBrandPreference || false,
      };

      console.log('📤 [SEGMENT] Evento "Dealer Preferences Saved":', eventData);
      
      window.analytics.track('Dealer Preferences Saved', eventData);
    } else {
      console.warn('⚠️ Segment analytics não está disponível');
    }
  } catch (error) {
    console.error('❌ Erro ao enviar evento de preferências para o Segment:', error);
  }
};

/**
 * Formata as preferências do formato da API/Redux para o formato do evento Segment
 * @param {Array} userPreferences - Array de preferências do usuário
 * @param {Object} channelOfContact - Objeto com canais de contato
 * @returns {Object} Objeto com as preferências formatadas para o evento
 */
export const formatPreferencesForTracking = (userPreferences, channelOfContact) => {
  // Mapear preferências para formato mais legível
  const prefsMap = userPreferences.reduce((acc, pref) => {
    acc[pref.name] = { isActive: pref.isActive, value: pref.value };
    return acc;
  }, {});

  // Determinar condição estrutural
  let structuralCondition = 'qualquer_cautelar';
  if (prefsMap.CAUTELAR_APROVADA?.isActive) {
    structuralCondition = 'cautelar_aprovada';
  }

  // Determinar preferência de leilão
  let auctionPreference = 'qualquer_cautelar';
  if (prefsMap.ACCEPT_AUCTION?.isActive) {
    auctionPreference = 'sim_leilao';
  } else if (prefsMap.ACCEPT_AUCTION_TYPE_A?.isActive) {
    auctionPreference = 'leilao_tipo_a';
  } else if (prefsMap.REJECT_AUCTION?.isActive) {
    auctionPreference = 'nao_leilao';
  }

  // Determinar condição do motor
  let engineCondition = 'sem_apontamentos_motor';
  if (prefsMap.ENGINE_WITH_ISSUES?.isActive) {
    engineCondition = 'com_apontamentos_motor';
  }

  // Determinar condição do câmbio
  let transmissionCondition = 'sem_apontamentos_cambio';
  if (prefsMap.TRANSMISSION_WITH_ISSUES?.isActive) {
    transmissionCondition = 'com_apontamentos_cambio';
  }

  // Determinar preferência de blindagem
  let armoredPreference = 'nao_blindados';
  if (prefsMap.ARMORED_AND_COMMON?.isActive) {
    armoredPreference = 'blindados_comuns';
  } else if (prefsMap.ONLY_ARMORED?.isActive) {
    armoredPreference = 'somente_blindados';
  } else if (prefsMap.NO_ARMORED?.isActive) {
    armoredPreference = 'nao_blindados';
  }

  // Determinar preferência de km
  let kmPreference = 'indiferente';
  let kmMaxValue = null;
  if (prefsMap.KM?.isActive) {
    const kmValue = prefsMap.KM.value;
    kmMaxValue = kmValue;
    if (kmValue === 100000) {
      kmPreference = 'ate_100k';
    } else if (kmValue === 150000) {
      kmPreference = 'ate_150k';
    } else if (kmValue === 200000) {
      kmPreference = 'ate_200k';
    }
  }

  // Determinar preferência de idade
  let agePreference = 'indiferente';
  let ageMinYear = null;
  if (prefsMap.YEAR?.isActive && prefsMap.YEAR.value) {
    agePreference = 'especifica';
    ageMinYear = prefsMap.YEAR.value;
  }

  // Canais de notificação
  // channelOfContact vem como array: [{ name: "WHATSAPP", isActive: true }, ...]
  let channelWhatsapp = false;
  let channelEmail = false;
  let channelPushNotification = false;

  if (Array.isArray(channelOfContact)) {
    channelOfContact.forEach(channel => {
      if (channel.name === 'WHATSAPP' || channel.name === 'whatsapp') {
        channelWhatsapp = channel.isActive || false;
      } else if (channel.name === 'EMAIL' || channel.name === 'email') {
        channelEmail = channel.isActive || false;
      } else if (channel.name === 'PUSH_NOTIFICATION' || channel.name === 'push_notification') {
        channelPushNotification = channel.isActive || false;
      }
    });
  }

  // Extrair marcas selecionadas
  // As marcas vêm como objetos com { name: "GroupName", isActive: boolean, value: [brands] }
  const brandPreferences = userPreferences.filter(
    pref => pref.value && Array.isArray(pref.value) && pref.value.length > 0
  );
  
  // Coletar todas as marcas de todos os grupos
  const selectedBrands = [];
  brandPreferences.forEach(pref => {
    if (pref.isActive && Array.isArray(pref.value)) {
      selectedBrands.push(...pref.value);
    }
  });

  const selectedBrandsCount = selectedBrands.length;
  const hasBrandPreference = selectedBrandsCount > 0;

  return {
    structuralCondition,
    auctionPreference,
    engineCondition,
    transmissionCondition,
    armoredPreference,
    kmPreference,
    kmMaxValue,
    agePreference,
    ageMinYear,
    channelWhatsapp,
    channelEmail,
    channelPushNotification,
    selectedBrands,
    selectedBrandsCount,
    hasBrandPreference,
  };
};
